/**
 * Frontend mirror of server/services/photoUrlSql.js#googlePhotoProxyUrl.
 *
 * Some response shapes still arrive with raw Google Place photo references
 * (e.g. "places/ChIJ.../photos/AcnlKN..."). When that happens, build the proxy
 * URL on the client. The single helper here means slash-encoding lives in one
 * place and the URL shape can evolve in one place.
 *
 * Backend SQL helpers (photoUrlSql.js) already produce proxy URLs for queries
 * that go through the cascade — so most fields land here as ready-to-use URLs.
 * This helper is for the legacy paths and for places where the API returns
 * raw references (search results, occasionally).
 */

import { API_BASE } from '../config/api';

/**
 * Returns true when the value looks like a Google Place photo reference
 * (places/{place_id}/photos/{ref}) rather than a usable URL.
 */
export function isGooglePlacesPhotoRef(value) {
  if (!value || typeof value !== 'string') return false;
  return value.startsWith('places/') && value.includes('/photos/');
}

/**
 * Build a proxy URL for a Google Place photo reference. Slashes are
 * URL-encoded so the query string survives Express's qs parser intact.
 *
 * @param {string} photoRef - e.g. "places/ChIJ.../photos/AcnlKN..."
 * @param {string} [placeId=''] - Used by the proxy to short-circuit to a
 *   user-photo if one exists for the place. Optional but recommended.
 * @returns {string}
 */
export function buildGooglePhotoProxyUrl(photoRef, placeId = '') {
  return `${API_BASE}/api/places/photo?photo_reference=${encodeURIComponent(photoRef)}&place_id=${placeId || ''}`;
}

/**
 * Returns true when a URL renders Google Maps Content — either our photo proxy
 * (`/api/places/photo?…`, which fetches from Google on demand) or a raw place
 * photo reference that would be turned into one.
 *
 * This exists for the non-Google basemaps. Google Maps Platform's terms forbid
 * displaying Google Maps Content alongside a non-Google map, so any surface
 * drawing markers on our MapLibre basemap has to drop these photos and fall
 * back to something else. PlacesMap enforces it centrally so a new call site
 * can't reintroduce the violation by forgetting.
 *
 * @param {string} url
 * @returns {boolean}
 */
export function isGooglePhotoUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (isGooglePlacesPhotoRef(url)) return true;
  // Absolute or relative: API_BASE is prepended in some call paths, so match
  // on the path rather than the start of the string.
  return url.includes('/api/places/photo');
}
